import { Cluster, ClusterSupportLevel } from 'api/models';
import { SUPPORT_LEVEL_MAP } from 'constants/kubernetes-support-map';
import { subDays } from 'date-fns';
import * as semver from 'semver';

export function filterClustersByVersion(
  clusters: Cluster[],
  version: string,
): Cluster[] {
  if (version === 'earlier') {
    return clusters.filter((c) =>
      semver.satisfies(semver.coerce(c.version) || '', '<1.22.0'),
    );
  }

  return clusters.filter((c) =>
    semver.satisfies(semver.coerce(c.version) || '', `${version}.x`),
  );
}

export const CLUSTERS: Cluster[] = [
  //region 1.29
  {
    id: 'id-eks-129-euw2-dev1',
    internal_k8s_ref: '',
    account_id: '',
    status: 'active',
    node_count: 42,
    managed_ng_count: 1,
    self_managed_ng_count: 0,
    container_runtimes: [],
    labels: {},
    rescan: 'completed',
    chkk_metadata: {
      version: '',
      git_commit: '',
      build_date: '',
      config: {},
      namespace: '',
    },
    support_level: SUPPORT_LEVEL_MAP['EKS']['1.29'] as ClusterSupportLevel[],
    name: 'eks-129-euw2-dev1',
    version: '1.29',
    environment: 'Dev',
    cloud_provider: 'EKS',
    region: 'eu-west-2',
    platform: {
      cloud_platform: 'AWS',
      container_runtimes: ['containerd'],
      k8s_distribution: 'EKS',
      managed_ng_count: 2,
      self_managed_ng_count: 0,
      region: 'us-east-2',
    },
    created: new Date(2024, 1, 8).getTime() / 1000,
    eol_date: new Date(2026, 2, 23).getTime() / 1000, // 2026-03-23
    k8s_release_date: new Date().getTime() / 1000,
    k8s_provider_release: new Date().getTime() / 1000,
  },
  {
    id: 'id-eks-129-euw2-dev2',
    internal_k8s_ref: '',
    account_id: '',
    status: 'active',
    node_count: 42,
    managed_ng_count: 1,
    self_managed_ng_count: 0,
    container_runtimes: [],
    labels: {},
    rescan: 'completed',
    chkk_metadata: {
      version: '',
      git_commit: '',
      build_date: '',
      config: {},
      namespace: '',
    },
    support_level: SUPPORT_LEVEL_MAP['EKS']['1.29'] as ClusterSupportLevel[],
    name: 'eks-129-euw2-dev2',
    version: '1.29',
    environment: 'Dev',
    cloud_provider: 'EKS',
    region: 'eu-west-2',
    platform: {
      cloud_platform: 'AWS',
      container_runtimes: ['containerd'],
      k8s_distribution: 'EKS',
      managed_ng_count: 2,
      self_managed_ng_count: 0,
      region: 'us-east-2',
    },
    created: new Date(2024, 1, 8).getTime() / 1000,
    eol_date: new Date(2026, 2, 23).getTime() / 1000, // 2026-03-23
    k8s_release_date: new Date().getTime() / 1000,
    k8s_provider_release: new Date().getTime() / 1000,
  },
  {
    id: 'id-gke-129-euw1-prod',
    internal_k8s_ref: '',
    account_id: '',
    status: 'active',
    node_count: 30,
    managed_ng_count: 3,
    self_managed_ng_count: 0,
    container_runtimes: ['containerd'],
    labels: {},
    rescan: 'completed',
    chkk_metadata: {
      version: '',
      git_commit: '',
      build_date: '',
      config: {},
      namespace: '',
    },
    support_level: SUPPORT_LEVEL_MAP['GKE']['1.29'] as ClusterSupportLevel[],
    name: 'gke-129-euw1-prod',
    version: '1.29',
    environment: 'Prod',
    cloud_provider: 'GKE',
    region: 'europe-west1',
    created: new Date(2024, 6, 5).getTime() / 1000,
    eol_date: new Date(2026, 0, 25).getTime() / 1000, // 2026-01-25
    k8s_release_date: new Date(2023, 11, 13).getTime() / 1000,
    k8s_provider_release: new Date(2024, 6, 9).getTime() / 1000,
    platform: {
      cloud_platform: 'GCP',
      container_runtimes: ['containerd'],
      k8s_distribution: 'GKE',
      managed_ng_count: 3,
      self_managed_ng_count: 0,
      region: 'europe-west1',
    },
  },
  //endregion
  //region 1.30
  {
    id: 'id-aks-130-eus2-prod',
    internal_k8s_ref: '',
    account_id: '',
    status: 'active',
    node_count: 22,
    managed_ng_count: 2,
    self_managed_ng_count: 0,
    container_runtimes: ['containerd'],
    labels: {},
    rescan: 'completed',
    chkk_metadata: {
      version: '',
      git_commit: '',
      build_date: '',
      config: {},
      namespace: '',
    },
    support_level: SUPPORT_LEVEL_MAP['AKS']['1.30'] as ClusterSupportLevel[],
    name: 'aks-130-eus2-prod',
    version: '1.30',
    environment: 'Prod',
    cloud_provider: 'AKS',
    region: 'eastus2',
    created: new Date(2025, 6, 20).getTime() / 1000,
    eol_date: new Date(2026, 6, 31).getTime() / 1000, // 2026-07-31
    k8s_release_date: new Date(2024, 3, 18).getTime() / 1000,
    k8s_provider_release: new Date(2024, 6, 1).getTime() / 1000,
    platform: {
      cloud_platform: 'Azure',
      container_runtimes: ['containerd'],
      k8s_distribution: 'AKS',
      managed_ng_count: 2,
      self_managed_ng_count: 0,
      region: 'eastus2',
    },
  },
  {
    id: 'id-eks-130-use2-staging',
    internal_k8s_ref: '',
    account_id: '',
    status: 'active',
    node_count: 25,
    managed_ng_count: 2,
    self_managed_ng_count: 0,
    container_runtimes: ['containerd'],
    labels: {},
    rescan: 'completed',
    chkk_metadata: {
      version: '',
      git_commit: '',
      build_date: '',
      config: {},
      namespace: '',
    },
    support_level: SUPPORT_LEVEL_MAP['EKS']['1.30'] as ClusterSupportLevel[],
    name: 'eks-130-use2-staging',
    version: '1.30',
    environment: 'Staging',
    cloud_provider: 'EKS',
    region: 'us-east-2',
    created: new Date(2025, 2, 1).getTime() / 1000,
    eol_date: new Date(2026, 6, 23).getTime() / 1000, // 2026-07-23
    k8s_release_date: new Date(2024, 3, 17).getTime() / 1000,
    k8s_provider_release: new Date(2024, 4, 23).getTime() / 1000,
    platform: {
      cloud_platform: 'AWS',
      container_runtimes: ['containerd'],
      k8s_distribution: 'EKS',
      managed_ng_count: 2,
      self_managed_ng_count: 0,
      region: 'us-east-2',
    },
  },
  //endregion
  //region 1.31
  {
    id: 'id-aks-131-cus1-dev',
    internal_k8s_ref: '',
    account_id: '',
    status: 'active',
    node_count: 15,
    managed_ng_count: 1,
    self_managed_ng_count: 0,
    container_runtimes: ['containerd'],
    labels: {},
    rescan: 'completed',
    chkk_metadata: {
      version: '',
      git_commit: '',
      build_date: '',
      config: {},
      namespace: '',
    },
    support_level: SUPPORT_LEVEL_MAP['AKS']['1.31'] as ClusterSupportLevel[],
    name: 'aks-131-cus1-dev',
    version: '1.31',
    environment: 'Dev',
    cloud_provider: 'AKS',
    region: 'centralus',
    created: new Date(2024, 10, 10).getTime() / 1000,
    eol_date: new Date(2026, 10, 30).getTime() / 1000, // 2026-11-30
    k8s_release_date: new Date(2024, 7, 13).getTime() / 1000,
    k8s_provider_release: new Date(2024, 9, 1).getTime() / 1000,
    platform: {
      cloud_platform: 'Azure',
      container_runtimes: ['containerd'],
      k8s_distribution: 'AKS',
      managed_ng_count: 1,
      self_managed_ng_count: 0,
      region: 'centralus',
    },
  },
  {
    id: 'id-eks-131-usw1-prod',
    internal_k8s_ref: '',
    account_id: '',
    status: 'active',
    node_count: 52,
    managed_ng_count: 22,
    self_managed_ng_count: 0,
    container_runtimes: [],
    labels: {},
    rescan: 'completed',
    chkk_metadata: {
      version: '',
      git_commit: '',
      build_date: '',
      config: {},
      namespace: '',
    },
    platform: {
      cloud_platform: 'AWS',
      container_runtimes: ['containerd'],
      k8s_distribution: 'EKS',
      managed_ng_count: 2,
      self_managed_ng_count: 0,
      region: 'us-west-1',
    },
    support_level: SUPPORT_LEVEL_MAP['EKS']['1.31'] as ClusterSupportLevel[],
    name: 'eks-131-usw1-prod',
    version: '1.31',
    environment: 'Prod',
    cloud_provider: 'EKS',
    region: 'us-west-1',
    created: new Date(2025, 9, 15).getTime() / 1000,
    eol_date: new Date(2025, 5, 11).getTime() / 1000, // 2025-06-11
    k8s_release_date: new Date().getTime() / 1000,
    k8s_provider_release: new Date().getTime() / 1000,
  },
  {
    id: 'id-gke-131-usw4-sandbox',
    internal_k8s_ref: '',
    account_id: '',
    status: 'active',
    node_count: 42,
    managed_ng_count: 1,
    self_managed_ng_count: 0,
    container_runtimes: [],
    labels: {},
    rescan: 'completed',
    chkk_metadata: {
      version: '',
      git_commit: '',
      build_date: '',
      config: {},
      namespace: '',
    },
    platform: {
      cloud_platform: 'GCP',
      container_runtimes: ['containerd'],
      k8s_distribution: 'GKE',
      managed_ng_count: 2,
      self_managed_ng_count: 0,
      region: 'us-west4',
    },
    support_level: SUPPORT_LEVEL_MAP['GKE']['1.31'] as ClusterSupportLevel[],
    name: 'gke-131-usw4-sandbox',
    version: '1.31',
    environment: 'Sandbox',
    cloud_provider: 'GKE',
    region: 'us-west4',
    created: subDays(new Date(), 60).getTime() / 1000,
    eol_date: new Date(2026, 0, 9).getTime() / 1000, // 2026-01-09
    k8s_release_date: new Date().getTime() / 1000,
    k8s_provider_release: new Date().getTime() / 1000,
  },
  //endregion
  //region 1.32
  {
    id: 'id-gke-132-euw2-staging',
    internal_k8s_ref: '',
    account_id: '',
    status: 'active',
    node_count: 20,
    managed_ng_count: 2,
    self_managed_ng_count: 0,
    container_runtimes: ['containerd'],
    labels: {},
    rescan: 'completed',
    chkk_metadata: {
      version: '',
      git_commit: '',
      build_date: '',
      config: {},
      namespace: '',
    },
    support_level: SUPPORT_LEVEL_MAP['GKE']['1.32'] as ClusterSupportLevel[],
    name: 'gke-132-euw2-staging',
    version: '1.32',
    environment: 'Staging',
    cloud_provider: 'GKE',
    region: 'europe-west2',
    created: new Date(2025, 1, 15).getTime() / 1000,
    eol_date: new Date(2027, 1, 11).getTime() / 1000, // 2027-02-11
    k8s_release_date: new Date(2024, 11, 11).getTime() / 1000,
    k8s_provider_release: new Date(2025, 0, 23).getTime() / 1000,
    platform: {
      cloud_platform: 'GCP',
      container_runtimes: ['containerd'],
      k8s_distribution: 'GKE',
      managed_ng_count: 2,
      self_managed_ng_count: 0,
      region: 'europe-west2',
    },
  },
  {
    id: 'id-aks-132-eu2-staging',
    internal_k8s_ref: '',
    account_id: '',
    status: 'active',
    node_count: 52,
    managed_ng_count: 22,
    self_managed_ng_count: 0,
    container_runtimes: [],
    labels: {},
    rescan: 'completed',
    chkk_metadata: {
      version: '',
      git_commit: '',
      build_date: '',
      config: {},
      namespace: '',
    },
    platform: {
      cloud_platform: 'Azure',
      container_runtimes: ['containerd'],
      k8s_distribution: 'AKS',
      managed_ng_count: 2,
      self_managed_ng_count: 0,
      region: 'eastus2',
    },
    support_level: SUPPORT_LEVEL_MAP['AKS']['1.32'] as ClusterSupportLevel[],
    name: 'aks-132-eus2-staging',
    version: '1.32',
    environment: 'Staging',
    cloud_provider: 'AKS',
    region: 'eastus2',
    created: new Date(2025, 9, 15).getTime() / 1000,
    eol_date: new Date(2024, 2, 31).getTime() / 1000, // 2024-03-31
    k8s_release_date: new Date().getTime() / 1000,
    k8s_provider_release: new Date().getTime() / 1000,
  },
  //endregion
  //region 1.33
  {
    id: 'id-eks-133-usw1-prod',
    internal_k8s_ref: '',
    account_id: '',
    status: 'active',
    node_count: 42,
    managed_ng_count: 1,
    self_managed_ng_count: 0,
    container_runtimes: [],
    labels: {},
    rescan: 'completed',
    chkk_metadata: {
      version: '',
      git_commit: '',
      build_date: '',
      config: {},
      namespace: '',
    },
    platform: {
      cloud_platform: 'AWS',
      container_runtimes: ['containerd'],
      k8s_distribution: 'EKS',
      managed_ng_count: 2,
      self_managed_ng_count: 0,
      region: 'us-west-1',
    },
    support_level: SUPPORT_LEVEL_MAP['EKS']['1.33'] as ClusterSupportLevel[],
    name: 'eks-133-usw1-prod',
    version: 'v1.33.4-eks-2d98532',
    environment: 'Prod',
    cloud_provider: 'EKS',
    region: 'us-west-1',
    created: new Date(2023, 11, 15).getTime() / 1000,
    eol_date: new Date(2025, 6, 24).getTime() / 1000, // 2025-07-24
    k8s_release_date: new Date().getTime() / 1000,
    k8s_provider_release: new Date().getTime() / 1000,
  },
  {
    id: 'id-gke-131-usw4-sandbox',
    internal_k8s_ref: '',
    account_id: '',
    status: 'active',
    node_count: 52,
    managed_ng_count: 22,
    self_managed_ng_count: 0,
    container_runtimes: [],
    labels: {},
    rescan: 'completed',
    chkk_metadata: {
      version: '',
      git_commit: '',
      build_date: '',
      config: {},
      namespace: '',
    },
    platform: {
      cloud_platform: 'GCP',
      container_runtimes: ['containerd'],
      k8s_distribution: 'GKE',
      managed_ng_count: 2,
      self_managed_ng_count: 0,
      region: 'us-west4',
    },
    support_level: SUPPORT_LEVEL_MAP['GKE']['1.33'] as ClusterSupportLevel[],
    name: 'gke-133-usw4-sandbox',
    version: '1.33',
    environment: 'Sandbox',
    cloud_provider: 'GKE',
    region: 'us-west4',
    created: new Date(2023, 11, 25).getTime() / 1000,
    eol_date: new Date(2025, 5, 14).getTime() / 1000, // 2025-06-14
    k8s_release_date: new Date().getTime() / 1000,
    k8s_provider_release: new Date().getTime() / 1000,
  },
  //endregion
];
